import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { 
  Shield, ArrowLeft, ExternalLink, LogOut, BarChart3, Users, 
  Package, DollarSign, Activity, Gavel, Tag, MessageCircle, 
  UserPlus, ShoppingBag, Search, Link, Loader2, Image, Check, X, Trash2, Eye, Truck, MapPin,
  AlertTriangle, ShieldCheck, Clock, FileCheck, Settings, Key, CreditCard, Save
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import api from "@/utils/api";

// Import Tab Component
const ImportTab = () => {
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [editableData, setEditableData] = useState(null);
  const [createdListing, setCreatedListing] = useState(null);
  const [error, setError] = useState(null);

  // Categories that require special shipping (freight/oversized)
  const OVERSIZED_CATEGORIES = ['Motors', 'Furniture', 'Heavy Equipment', 'Boats', 'RVs'];
  const OVERSIZED_KEYWORDS = ['car', 'truck', 'motorcycle', 'boat', 'rv', 'trailer', 'tractor', 'atv', 'vehicle', 'furniture', 'sofa', 'couch', 'bed', 'piano', 'appliance', 'refrigerator', 'washer', 'dryer'];

  // Shipping companies for large items
  const FREIGHT_SHIPPERS = [
    { name: 'uShip', url: 'https://www.uship.com', desc: 'Marketplace for shipping large items' },
    { name: 'Montway Auto Transport', url: 'https://www.montway.com', desc: 'Vehicle shipping specialists' },
    { name: 'Ship a Car Direct', url: 'https://www.shipacardirect.com', desc: 'Auto transport services' },
    { name: 'FreightCenter', url: 'https://www.freightcenter.com', desc: 'Freight & LTL shipping' },
    { name: 'GoShip', url: 'https://www.goship.com', desc: 'Affordable freight shipping' }
  ];

  const isOversizedItem = (data) => {
    if (!data) return false;
    const category = (data.category || '').toLowerCase();
    const title = (data.title || '').toLowerCase();
    
    // Check if category is oversized
    if (OVERSIZED_CATEGORIES.some(cat => category.includes(cat.toLowerCase()))) return true;
    
    // Check if title contains oversized keywords
    if (OVERSIZED_KEYWORDS.some(kw => title.includes(kw))) return true;
    
    // Check if price suggests large item (e.g., > $5000 for motors)
    if (category === 'motors' || data.price > 10000) return true;
    
    return false;
  };

  const handleExtract = async (e) => {
    e.preventDefault();
    if (!url.trim()) {
      toast.error('Please enter a URL');
      return;
    }

    setLoading(true);
    setError(null);
    setExtractedData(null);
    setEditableData(null);
    setCreatedListing(null);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await api.post('/admin/import-from-url', 
        { url: url.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const data = response.data.extracted_data;
      // Add default location and shipping fields
      const enhancedData = {
        ...data,
        location: {
          city: '',
          state: '',
          country: 'United States',
          zip_code: ''
        },
        shipping: {
          type: isOversizedItem(data) ? 'freight_quote' : 'standard',
          local_pickup: true,
          free_shipping: false,
          local_rate: 0,
          national_rate: 0
        },
        seller_name: '',  // Admin can specify seller name
        seller_email: ''  // Optional seller email
      };
      setExtractedData(enhancedData);
      setEditableData({ ...enhancedData });
      toast.success(response.data.message || 'Data extracted successfully!');
    } catch (error) {
      console.error('Import error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Import failed';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateListing = async () => {
    if (!editableData) return;

    setCreating(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await api.post('/admin/create-from-import', editableData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCreatedListing(response.data);
      toast.success('Listing created successfully!');
    } catch (error) {
      console.error('Create listing error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to create listing';
      toast.error(errorMsg);
    } finally {
      setCreating(false);
    }
  };

  const handleEditField = (field, value) => {
    setEditableData(prev => ({ ...prev, [field]: value }));
  };

  const handleLocationChange = (field, value) => {
    setEditableData(prev => ({
      ...prev,
      location: { ...prev.location, [field]: value }
    }));
  };

  const handleShippingChange = (field, value) => {
    setEditableData(prev => ({
      ...prev,
      shipping: { ...prev.shipping, [field]: value }
    }));
  };

  const resetForm = () => {
    setUrl('');
    setExtractedData(null);
    setEditableData(null);
    setCreatedListing(null);
    setError(null);
  };

  const isOversized = editableData ? isOversizedItem(editableData) : false;

  return (
    <div className="space-y-6">
      {/* URL Input Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link className="h-5 w-5" />
            URL Import Tool
          </CardTitle>
          <p className="text-muted-foreground">
            Import product listings from external URLs (eBay, Amazon, Walmart, etc.)
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleExtract} className="space-y-4">
            <div>
              <Label htmlFor="import-url">Product URL</Label>
              <Input
                id="import-url"
                type="url"
                placeholder="https://www.ebay.com/itm/... or https://www.amazon.com/dp/..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                disabled={loading || createdListing}
                className="mt-1"
                data-testid="admin-import-url-input"
              />
              <p className="text-sm text-muted-foreground mt-1">
                Paste any product URL from eBay, Amazon, Walmart, Etsy, and more
              </p>
            </div>
            
            <Button 
              type="submit" 
              disabled={loading || !url.trim() || createdListing}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="admin-import-extract-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Extracting Data...
                </>
              ) : (
                <>
                  <Link className="h-4 w-4 mr-2" />
                  Extract Product Data
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-700">
              <X className="h-4 w-4" />
              <span className="font-medium">Import Failed</span>
            </div>
            <p className="text-red-600 mt-1">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Created Listing Success */}
      {createdListing && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-emerald-700">
              <Check className="h-5 w-5" />
              Listing Created Successfully!
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-4 border">
                <div className="flex gap-4">
                  {createdListing.listing?.images?.[0] && (
                    <img 
                      src={createdListing.listing.images[0]} 
                      alt={createdListing.listing.title}
                      className="w-24 h-24 object-cover rounded-lg border"
                    />
                  )}
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{createdListing.listing?.title}</h3>
                    <p className="text-emerald-600 font-bold mt-1">
                      ${createdListing.listing?.price?.toFixed(2)}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="secondary">{createdListing.listing?.category}</Badge>
                      <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => window.open(`/item/${createdListing.listing_id}`, '_blank')}
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  View Listing
                </Button>
                <Button 
                  onClick={resetForm}
                  className="bg-emerald-500 hover:bg-emerald-600"
                >
                  Import Another
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Extracted Data - Editable Form */}
      {editableData && !createdListing && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Review & Edit Extracted Data
            </CardTitle>
            <p className="text-muted-foreground">
              Imported from <Badge variant="secondary">{extractedData.source_platform}</Badge>
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Images Preview */}
              {editableData.images?.length > 0 && (
                <div>
                  <Label>Product Images ({editableData.images.length})</Label>
                  <div className="flex gap-2 mt-2 overflow-x-auto pb-2">
                    {editableData.images.map((img, i) => (
                      <img 
                        key={i}
                        src={img} 
                        alt={`Product ${i+1}`}
                        className="w-20 h-20 object-cover rounded-lg border flex-shrink-0"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Seller Information */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-semibold mb-3 flex items-center gap-2 text-blue-800">
                  <Users className="h-4 w-4" />
                  Seller Information
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="seller-name">Seller Name *</Label>
                    <Input
                      id="seller-name"
                      placeholder="Enter seller name"
                      value={editableData.seller_name || ''}
                      onChange={(e) => handleEditField('seller_name', e.target.value)}
                      className="mt-1 bg-white"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Name displayed on the listing
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="seller-email">Seller Email (optional)</Label>
                    <Input
                      id="seller-email"
                      type="email"
                      placeholder="seller@example.com"
                      value={editableData.seller_email || ''}
                      onChange={(e) => handleEditField('seller_email', e.target.value)}
                      className="mt-1 bg-white"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      For contact purposes
                    </p>
                  </div>
                </div>
              </div>

              {/* Title */}
              <div>
                <Label htmlFor="edit-title">Title *</Label>
                <Input
                  id="edit-title"
                  value={editableData.title}
                  onChange={(e) => handleEditField('title', e.target.value)}
                  className="mt-1"
                />
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  value={editableData.description}
                  onChange={(e) => handleEditField('description', e.target.value)}
                  className="mt-1"
                  rows={4}
                />
              </div>

              {/* Price & Category */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-price">Price ($) *</Label>
                  <Input
                    id="edit-price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={editableData.price || ''}
                    onChange={(e) => handleEditField('price', parseFloat(e.target.value) || 0)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-category">Category</Label>
                  <select
                    id="edit-category"
                    value={editableData.category}
                    onChange={(e) => handleEditField('category', e.target.value)}
                    className="mt-1 w-full h-10 px-3 border rounded-md bg-background"
                  >
                    <option value="Electronics">Electronics</option>
                    <option value="Fashion">Fashion</option>
                    <option value="Home & Garden">Home & Garden</option>
                    <option value="Toys">Toys</option>
                    <option value="Collectibles">Collectibles</option>
                    <option value="Motors">Motors</option>
                    <option value="Sporting Goods">Sporting Goods</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              {/* Condition & Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-condition">Condition</Label>
                  <select
                    id="edit-condition"
                    value={editableData.condition}
                    onChange={(e) => handleEditField('condition', e.target.value)}
                    className="mt-1 w-full h-10 px-3 border rounded-md bg-background"
                  >
                    <option value="new">New</option>
                    <option value="like_new">Like New</option>
                    <option value="good">Good</option>
                    <option value="used">Used</option>
                    <option value="refurbished">Refurbished</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="edit-type">Listing Type</Label>
                  <select
                    id="edit-type"
                    value={editableData.listing_type}
                    onChange={(e) => handleEditField('listing_type', e.target.value)}
                    className="mt-1 w-full h-10 px-3 border rounded-md bg-background"
                  >
                    <option value="fixed">Fixed Price</option>
                    <option value="auction">Auction</option>
                  </select>
                </div>
              </div>

              {/* Location Section */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-emerald-500" />
                  Item Location
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-city">City</Label>
                    <Input
                      id="edit-city"
                      placeholder="Los Angeles"
                      value={editableData.location?.city || ''}
                      onChange={(e) => handleLocationChange('city', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-state">State</Label>
                    <Input
                      id="edit-state"
                      placeholder="CA"
                      value={editableData.location?.state || ''}
                      onChange={(e) => handleLocationChange('state', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-zip">ZIP Code</Label>
                    <Input
                      id="edit-zip"
                      placeholder="90001"
                      value={editableData.location?.zip_code || ''}
                      onChange={(e) => handleLocationChange('zip_code', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-country">Country</Label>
                    <Input
                      id="edit-country"
                      value={editableData.location?.country || 'United States'}
                      onChange={(e) => handleLocationChange('country', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Shipping Section */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Truck className="h-4 w-4 text-blue-500" />
                  Shipping Options
                </h4>

                {/* Check if oversized item */}
                {isOversized ? (
                  <div className="space-y-4">
                    {/* Freight Quote Banner */}
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Truck className="h-6 w-6 text-amber-600 mt-0.5" />
                        <div>
                          <h5 className="font-semibold text-amber-800">Large Item - Get a Quote for Shipping</h5>
                          <p className="text-sm text-amber-700 mt-1">
                            This item requires special shipping arrangements. Buyers will need to contact a freight carrier for shipping quotes.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Shipping Companies */}
                    <div>
                      <Label className="mb-2 block">Recommended Shipping Companies</Label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {FREIGHT_SHIPPERS.map((shipper) => (
                          <a
                            key={shipper.name}
                            href={shipper.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-3 bg-white border rounded-lg hover:bg-slate-50 hover:border-blue-300 transition"
                          >
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <Truck className="h-5 w-5 text-blue-600" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-sm text-blue-600">{shipper.name}</p>
                              <p className="text-xs text-muted-foreground truncate">{shipper.desc}</p>
                            </div>
                            <ExternalLink className="h-4 w-4 text-slate-400 flex-shrink-0" />
                          </a>
                        ))}
                      </div>
                    </div>

                    {/* Local Pickup Option */}
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <input
                        type="checkbox"
                        id="local-pickup-oversized"
                        checked={editableData.shipping?.local_pickup}
                        onChange={(e) => handleShippingChange('local_pickup', e.target.checked)}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="local-pickup-oversized" className="cursor-pointer">
                        Local pickup available
                      </Label>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Shipping Type */}
                    <div>
                      <Label htmlFor="shipping-type">Shipping Type</Label>
                      <select
                        id="shipping-type"
                        value={editableData.shipping?.type || 'standard'}
                        onChange={(e) => handleShippingChange('type', e.target.value)}
                        className="mt-1 w-full h-10 px-3 border rounded-md bg-background"
                      >
                        <option value="standard">Standard Shipping</option>
                        <option value="free">Free Shipping</option>
                        <option value="local_only">Local Pickup Only</option>
                        <option value="freight_quote">Freight - Get a Quote</option>
                      </select>
                    </div>

                    {/* Standard Shipping Rates */}
                    {editableData.shipping?.type === 'standard' && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="local-rate">Local Shipping ($)</Label>
                          <Input
                            id="local-rate"
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="5.99"
                            value={editableData.shipping?.local_rate || ''}
                            onChange={(e) => handleShippingChange('local_rate', parseFloat(e.target.value) || 0)}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="national-rate">National Shipping ($)</Label>
                          <Input
                            id="national-rate"
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="9.99"
                            value={editableData.shipping?.national_rate || ''}
                            onChange={(e) => handleShippingChange('national_rate', parseFloat(e.target.value) || 0)}
                            className="mt-1"
                          />
                        </div>
                      </div>
                    )}

                    {/* Local Pickup Option */}
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="local-pickup"
                        checked={editableData.shipping?.local_pickup}
                        onChange={(e) => handleShippingChange('local_pickup', e.target.checked)}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="local-pickup" className="cursor-pointer">
                        Allow local pickup
                      </Label>
                    </div>
                  </div>
                )}
              </div>

              {/* Source Info */}
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <p className="text-muted-foreground">
                  <strong>Source:</strong> {extractedData.source_url}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleCreateListing}
                  disabled={creating || !editableData.title || editableData.price <= 0}
                  className="bg-emerald-500 hover:bg-emerald-600"
                  data-testid="admin-import-create-btn"
                >
                  {creating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating Listing...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Create Listing on BidZuu
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Import Instructions */}
      {!extractedData && !createdListing && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">How to Use URL Import</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xs font-bold">1</div>
                <div>
                  <p className="font-medium">Copy Product URL</p>
                  <p className="text-muted-foreground">Find a product on eBay, Amazon, or other supported sites and copy its URL</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xs font-bold">2</div>
                <div>
                  <p className="font-medium">Extract & Review</p>
                  <p className="text-muted-foreground">The system extracts title, description, images, and pricing automatically</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xs font-bold">3</div>
                <div>
                  <p className="font-medium">Edit & Publish</p>
                  <p className="text-muted-foreground">Review the extracted data, make edits if needed, and create the listing</p>
                </div>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-blue-700 text-sm">
                <strong>Supported Sites:</strong> eBay, Amazon, Walmart, Etsy, AliExpress, Mercari, Poshmark, and more
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// TikTok & YouTube Videos Management Tab
const TikTokManagementTab = () => {
  const [tiktokUrl, setTiktokUrl] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [youtubeTitle, setYoutubeTitle] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [videos, setVideos] = useState([]);
  const [fetchingVideos, setFetchingVideos] = useState(true);
  const [activeVideoTab, setActiveVideoTab] = useState('youtube');

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await api.get('/entertainment/feed');
      setVideos(response.data.videos || []);
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setFetchingVideos(false);
    }
  };

  const extractTikTokId = (url) => {
    const match = url.match(/\/video\/(\d+)/);
    return match ? match[1] : null;
  };

  const extractYouTubeId = (url) => {
    // Handle various YouTube URL formats
    let match = url.match(/[?&]v=([^&]+)/);
    if (match) return match[1];
    
    match = url.match(/youtu\.be\/([^?&]+)/);
    if (match) return match[1];
    
    match = url.match(/\/shorts\/([^?&]+)/);
    if (match) return match[1];
    
    match = url.match(/\/embed\/([^?&]+)/);
    if (match) return match[1];
    
    return null;
  };

  const handleAddTikTok = async () => {
    if (!tiktokUrl) {
      toast.error('Please enter a TikTok video URL');
      return;
    }

    const videoId = extractTikTokId(tiktokUrl);
    if (!videoId) {
      toast.error('Invalid TikTok URL');
      return;
    }

    setLoading(true);
    try {
      await api.post('/entertainment/tiktok/add-video', {
        video_url: tiktokUrl,
        description: description
      });
      toast.success('TikTok video added!');
      setTiktokUrl('');
      setDescription('');
      fetchVideos();
    } catch (error) {
      toast.error('Failed to add video');
    } finally {
      setLoading(false);
    }
  };

  const handleAddYouTube = async () => {
    if (!youtubeUrl) {
      toast.error('Please enter a YouTube video URL');
      return;
    }

    const videoId = extractYouTubeId(youtubeUrl);
    if (!videoId) {
      toast.error('Invalid YouTube URL');
      return;
    }

    setLoading(true);
    try {
      await api.post('/entertainment/youtube/add-video', {
        video_url: youtubeUrl,
        title: youtubeTitle
      });
      toast.success('YouTube video added!');
      setYoutubeUrl('');
      setYoutubeTitle('');
      fetchVideos();
    } catch (error) {
      toast.error('Failed to add video');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Add Video Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-pink-500" />
            Add Videos to Entertainment Feed
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Tab Selection */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setActiveVideoTab('youtube')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
                activeVideoTab === 'youtube' ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              YouTube
            </button>
            <button
              onClick={() => setActiveVideoTab('tiktok')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
                activeVideoTab === 'tiktok' ? 'bg-pink-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
              </svg>
              TikTok
            </button>
          </div>

          {/* YouTube Form */}
          {activeVideoTab === 'youtube' && (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800 font-medium mb-2">🎬 Add YouTube Videos (Works instantly!)</p>
                <p className="text-sm text-red-700">Paste any YouTube video URL - regular videos, Shorts, or music videos</p>
              </div>
              
              <div>
                <Label>YouTube Video URL</Label>
                <Input
                  placeholder="https://www.youtube.com/watch?v=... or youtu.be/... or /shorts/..."
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                />
              </div>
              <div>
                <Label>Title (optional)</Label>
                <Input
                  placeholder="Video title"
                  value={youtubeTitle}
                  onChange={(e) => setYoutubeTitle(e.target.value)}
                />
              </div>
              <Button 
                onClick={handleAddYouTube} 
                disabled={loading || !youtubeUrl}
                className="bg-red-600 hover:bg-red-700"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Add YouTube Video
              </Button>

              {youtubeUrl && extractYouTubeId(youtubeUrl) && (
                <div className="mt-4">
                  <p className="text-sm font-medium mb-2">Preview:</p>
                  <div className="aspect-video max-w-md bg-black rounded-lg overflow-hidden">
                    <iframe
                      src={`https://www.youtube.com/embed/${extractYouTubeId(youtubeUrl)}`}
                      className="w-full h-full"
                      allowFullScreen
                      style={{ border: 'none' }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TikTok Form */}
          {activeVideoTab === 'tiktok' && (
            <div className="space-y-4">
              <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
                <p className="text-sm text-pink-800 font-medium mb-2">📱 Add TikTok Videos</p>
                <p className="text-sm text-pink-700">Note: TikTok videos must be public and may not always embed properly</p>
              </div>
              
              <div>
                <Label>TikTok Video URL</Label>
                <Input
                  placeholder="https://www.tiktok.com/@username/video/1234567890"
                  value={tiktokUrl}
                  onChange={(e) => setTiktokUrl(e.target.value)}
                />
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Input
                  placeholder="Add a description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              <Button 
                onClick={handleAddTikTok} 
                disabled={loading || !tiktokUrl}
                className="bg-pink-500 hover:bg-pink-600"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Add TikTok Video
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Current Videos */}
      <Card>
        <CardHeader>
          <CardTitle>Current Videos in Feed ({videos.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {fetchingVideos ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-pink-500" />
            </div>
          ) : videos.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Activity className="h-12 w-12 mx-auto mb-2 text-slate-300" />
              <p>No videos in feed yet. Add your first video above!</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {videos.map((video, index) => (
                <div key={video.video_id || index} className="bg-slate-100 rounded-lg p-3">
                  <div className={`${video.type === 'youtube' ? 'aspect-video' : 'aspect-[9/16] max-h-48'} bg-black rounded-lg overflow-hidden mb-2`}>
                    {video.type === 'youtube' ? (
                      <iframe
                        src={`https://www.youtube.com/embed/${video.video_id}`}
                        className="w-full h-full"
                        style={{ border: 'none' }}
                      />
                    ) : (
                      <iframe
                        src={`https://www.tiktok.com/embed/v2/${video.video_id}`}
                        className="w-full h-full"
                        style={{ border: 'none' }}
                      />
                    )}
                  </div>
                  <Badge className={video.type === 'youtube' ? 'bg-red-600' : 'bg-pink-500'}>
                    {video.type === 'youtube' ? 'YouTube' : 'TikTok'}
                  </Badge>
                  <p className="text-xs font-medium truncate mt-1">{video.title || video.username || 'Untitled'}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============ ANALYTICS TAB ============
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const AnalyticsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liveVisitors, setLiveVisitors] = useState([]);
  const [timeRange, setTimeRange] = useState('month');

  useEffect(() => {
    fetchAnalytics();
    const liveInterval = setInterval(fetchLiveVisitors, 30000);
    return () => clearInterval(liveInterval);
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await api.get('/admin/analytics/overview', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
      fetchLiveVisitors();
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const fetchLiveVisitors = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await api.get('/admin/analytics/live', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLiveVisitors(response.data.visitors || []);
    } catch (error) {
      console.error('Error fetching live visitors:', error);
    }
  };

  const CHART_COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#84cc16'];

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!data) {
    return (
      <Card className="p-8 text-center">
        <BarChart3 className="h-12 w-12 mx-auto mb-4 text-slate-300" />
        <p className="text-muted-foreground">No analytics data available yet.</p>
        <p className="text-sm text-muted-foreground mt-2">
          Analytics will appear once visitors start browsing your site.
        </p>
        <Button onClick={fetchAnalytics} className="mt-4 bg-emerald-500">
          Refresh Data
        </Button>
      </Card>
    );
  }

  const { overview, top_pages, traffic_sources, devices, browsers, countries, daily_data, hourly_data } = data;

  return (
    <div className="space-y-6" data-testid="admin-analytics-tab">
      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Live Now */}
        <Card className="border-2 border-emerald-500 bg-emerald-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-emerald-500 flex items-center justify-center">
                <Activity className="h-6 w-6 text-white animate-pulse" />
              </div>
              <div>
                <p className="text-3xl font-bold text-emerald-700">{overview?.live_visitors || 0}</p>
                <p className="text-sm text-emerald-600 font-medium">Live Now</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Today */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Eye className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{overview?.today?.views || 0}</p>
                <p className="text-sm text-muted-foreground">Today's Views</p>
                <p className="text-xs text-blue-600">{overview?.today?.visitors || 0} visitors</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* This Week */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                <BarChart3 className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{overview?.week?.views?.toLocaleString() || 0}</p>
                <p className="text-sm text-muted-foreground">This Week</p>
                <p className="text-xs text-purple-600">{overview?.week?.visitors || 0} visitors</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* This Month */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Users className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{overview?.month?.views?.toLocaleString() || 0}</p>
                <p className="text-sm text-muted-foreground">This Month</p>
                <p className="text-xs text-amber-600">{overview?.month?.visitors || 0} unique visitors</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Traffic Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <BarChart3 className="h-5 w-5 text-emerald-500" />
              Daily Traffic (Last 14 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={daily_data || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="_id" 
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => v ? v.slice(5) : ''}
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }}
                    labelFormatter={(v) => `Date: ${v}`}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="views" 
                    name="Page Views"
                    stroke="#10b981" 
                    strokeWidth={2}
                    dot={{ fill: '#10b981' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="visitors" 
                    name="Unique Visitors"
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    dot={{ fill: '#3b82f6' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Hourly Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Activity className="h-5 w-5 text-blue-500" />
              Today's Activity by Hour
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hourly_data || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="_id" 
                    tick={{ fontSize: 10 }}
                    tickFormatter={(v) => `${v}:00`}
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }}
                    labelFormatter={(v) => `${v}:00`}
                  />
                  <Bar dataKey="count" name="Views" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Second Row - Pie Charts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Device Types */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Device Types</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={devices || []}
                    dataKey="count"
                    nameKey="device"
                    cx="50%"
                    cy="50%"
                    outerRadius={60}
                    label={({ device, percent }) => `${device} ${(percent * 100).toFixed(0)}%`}
                  >
                    {(devices || []).map((_, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-2 justify-center mt-2">
              {(devices || []).map((d, i) => (
                <Badge key={d.device} variant="outline" style={{ borderColor: CHART_COLORS[i % CHART_COLORS.length] }}>
                  {d.device}: {d.count}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Browsers */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Browsers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={browsers || []}
                    dataKey="count"
                    nameKey="browser"
                    cx="50%"
                    cy="50%"
                    outerRadius={60}
                    label={({ browser, percent }) => `${browser} ${(percent * 100).toFixed(0)}%`}
                  >
                    {(browsers || []).map((_, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-2 justify-center mt-2">
              {(browsers || []).slice(0, 4).map((b, i) => (
                <Badge key={b.browser} variant="outline" style={{ borderColor: CHART_COLORS[i % CHART_COLORS.length] }}>
                  {b.browser}: {b.count}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Traffic Sources */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Traffic Sources</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={traffic_sources || []}
                    dataKey="count"
                    nameKey="source"
                    cx="50%"
                    cy="50%"
                    outerRadius={60}
                    label={({ source, percent }) => `${source} ${(percent * 100).toFixed(0)}%`}
                  >
                    {(traffic_sources || []).map((_, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-2 justify-center mt-2">
              {(traffic_sources || []).slice(0, 4).map((s, i) => (
                <Badge key={s.source} variant="outline" style={{ borderColor: CHART_COLORS[i % CHART_COLORS.length] }}>
                  {s.source}: {s.count}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Pages */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Eye className="h-5 w-5 text-emerald-500" />
              Top Pages (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(top_pages || []).length === 0 ? (
                <p className="text-muted-foreground text-center py-4">No page data yet</p>
              ) : (
                (top_pages || []).map((page, index) => (
                  <div key={page.page} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-xs font-bold">
                        {index + 1}
                      </span>
                      <span className="text-sm font-medium truncate max-w-[200px]" title={page.page}>
                        {page.page === '/' ? 'Homepage' : page.page}
                      </span>
                    </div>
                    <Badge variant="secondary">{page.views?.toLocaleString()} views</Badge>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Countries */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <MapPin className="h-5 w-5 text-blue-500" />
              Visitors by Country
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(countries || []).length === 0 ? (
                <p className="text-muted-foreground text-center py-4">No location data yet</p>
              ) : (
                (countries || []).map((country, index) => {
                  // Country flag mapping
                  const countryFlags = {
                    'United States': '🇺🇸',
                    'United Kingdom': '🇬🇧',
                    'Canada': '🇨🇦',
                    'Germany': '🇩🇪',
                    'France': '🇫🇷',
                    'Australia': '🇦🇺',
                    'Japan': '🇯🇵',
                    'China': '🇨🇳',
                    'India': '🇮🇳',
                    'Brazil': '🇧🇷',
                    'Mexico': '🇲🇽',
                    'Spain': '🇪🇸',
                    'Italy': '🇮🇹',
                    'Netherlands': '🇳🇱',
                    'Sweden': '🇸🇪',
                    'Norway': '🇳🇴',
                    'Denmark': '🇩🇰',
                    'Ireland': '🇮🇪',
                    'Poland': '🇵🇱',
                    'Russia': '🇷🇺',
                    'South Korea': '🇰🇷',
                    'Singapore': '🇸🇬',
                    'Unknown': '🌍'
                  };
                  const flag = countryFlags[country.country] || '🌍';
                  
                  return (
                    <div key={country.country} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold">
                          {index + 1}
                        </span>
                        <span className="text-lg">{flag}</span>
                        <span className="text-sm font-medium">{country.country || 'Unknown'}</span>
                      </div>
                      <Badge variant="secondary">{country.count?.toLocaleString()}</Badge>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Live Visitors */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Activity className="h-5 w-5 text-emerald-500 animate-pulse" />
            Live Visitors ({liveVisitors.length})
            <Button size="sm" variant="outline" onClick={fetchLiveVisitors} className="ml-auto">
              <Loader2 className="h-4 w-4 mr-1" /> Refresh
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {liveVisitors.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Activity className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>No active visitors right now</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {liveVisitors.map((visitor, i) => (
                <div key={visitor.session_id || i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="h-2 w-2 bg-emerald-500 rounded-full animate-pulse" />
                    <div>
                      <p className="text-sm font-medium">{visitor.page || '/'}</p>
                      <p className="text-xs text-muted-foreground">
                        {visitor.device_type} • {visitor.country || 'Unknown'}
                      </p>
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {visitor.last_activity ? new Date(visitor.last_activity).toLocaleTimeString() : 'Now'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============ PAYOUTS MANAGEMENT TAB ============
const PayoutsTab = () => {
  const [payouts, setPayouts] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showSettings, setShowSettings] = useState(false);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    fetchPayoutData();
  }, [filter]);

  const fetchPayoutData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [payoutsRes, statsRes, settingsRes] = await Promise.all([
        api.get(`/admin/payouts${filter !== 'all' ? `?status=${filter}` : ''}`, { headers }),
        api.get('/admin/payout-stats', { headers }),
        api.get('/admin/payout-settings', { headers })
      ]);
      
      setPayouts(payoutsRes.data.payouts || []);
      setStats(statsRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Error fetching payout data:', error);
      toast.error('Failed to load payout data');
    } finally {
      setLoading(false);
    }
  };

  const handlePayoutAction = async (payoutId, action) => {
    try {
      setProcessing(payoutId);
      const token = localStorage.getItem('adminToken');
      await api.post('/admin/payout-action', 
        { payout_id: payoutId, action },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(`Payout ${action}ed successfully`);
      fetchPayoutData();
    } catch (error) {
      console.error('Error processing payout:', error);
      toast.error('Failed to process payout');
    } finally {
      setProcessing(null);
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      const token = localStorage.getItem('adminToken');
      await api.post('/admin/payout-settings', newSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings updated');
      setSettings(newSettings);
      setShowSettings(false);
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      approved: 'bg-blue-100 text-blue-700',
      processing: 'bg-purple-100 text-purple-700',
      completed: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700',
      failed: 'bg-red-100 text-red-700'
    };
    return <Badge className={styles[status] || 'bg-slate-100'}>{status}</Badge>;
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-payouts-tab">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Requests</p>
                <p className="text-2xl font-bold">{stats?.pending_requests || 0}</p>
                <p className="text-sm text-amber-600">${(stats?.pending_amount || 0).toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Check className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Approved</p>
                <p className="text-2xl font-bold">{stats?.approved_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Paid Out</p>
                <p className="text-2xl font-bold">${(stats?.total_paid_out || 0).toFixed(2)}</p>
                <p className="text-sm text-emerald-600">{stats?.completed_count || 0} payouts</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Package className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Awaiting Delivery</p>
                <p className="text-2xl font-bold">{stats?.purchases_awaiting_delivery || 0}</p>
                <p className="text-sm text-muted-foreground">{stats?.hold_days || 3} day hold</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Settings */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {['all', 'pending', 'approved', 'completed', 'rejected'].map(status => (
            <Button
              key={status}
              variant={filter === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(status)}
              className={filter === status ? 'bg-emerald-500' : ''}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          ))}
        </div>
        <Button variant="outline" onClick={() => setShowSettings(!showSettings)}>
          ⚙️ Payout Settings
        </Button>
      </div>

      {/* Settings Panel */}
      {showSettings && settings && (
        <Card className="border-2 border-emerald-200">
          <CardHeader>
            <CardTitle>💰 Payout Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Hold Period (Days)</Label>
                <Input 
                  type="number" 
                  value={settings.hold_days || 3}
                  onChange={(e) => setSettings({...settings, hold_days: parseInt(e.target.value)})}
                  min={0}
                  max={30}
                />
                <p className="text-xs text-muted-foreground mt-1">Days to hold funds after delivery confirmation</p>
              </div>
              <div>
                <Label>Minimum Payout Amount ($)</Label>
                <Input 
                  type="number" 
                  value={settings.min_payout_amount || 10}
                  onChange={(e) => setSettings({...settings, min_payout_amount: parseFloat(e.target.value)})}
                  min={1}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => updateSettings(settings)} className="bg-emerald-500">
                Save Settings
              </Button>
              <Button variant="outline" onClick={() => setShowSettings(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payouts List */}
      <Card>
        <CardHeader>
          <CardTitle>Payout Requests</CardTitle>
        </CardHeader>
        <CardContent>
          {payouts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No payout requests found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {payouts.map((payout) => (
                <div key={payout.payout_id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <DollarSign className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium">{payout.seller_name}</p>
                      <p className="text-sm text-muted-foreground">{payout.seller_email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline">
                          {payout.payout_method === 'stripe' ? '💳 Stripe' : '🅿️ PayPal'}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {payout.paypal_email || payout.stripe_account_id?.slice(0, 15)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <p className="text-xl font-bold text-emerald-600">${payout.amount?.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(payout.requested_at).toLocaleDateString()}
                    </p>
                    {getStatusBadge(payout.status)}
                  </div>
                  
                  <div className="flex gap-2">
                    {payout.status === 'pending' && (
                      <>
                        <Button 
                          size="sm" 
                          className="bg-emerald-500"
                          onClick={() => handlePayoutAction(payout.payout_id, 'approve')}
                          disabled={processing === payout.payout_id}
                        >
                          {processing === payout.payout_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => handlePayoutAction(payout.payout_id, 'reject')}
                          disabled={processing === payout.payout_id}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    {payout.status === 'approved' && (
                      <Button 
                        size="sm" 
                        className="bg-blue-500"
                        onClick={() => handlePayoutAction(payout.payout_id, 'process')}
                        disabled={processing === payout.payout_id}
                      >
                        {processing === payout.payout_id ? <Loader2 className="h-4 w-4 animate-spin" /> : '💸 Process'}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============ BANNER ADS MANAGEMENT TAB ============
const BannerAdsTab = () => {
  const [ads, setAds] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    image_url: '',
    link_url: '',
    position: 'home_top',
    advertiser_name: '',
    advertiser_email: '',
    price_paid: '',
    start_date: '',
    end_date: '',
    is_active: true
  });

  const positions = [
    { value: 'home_top', label: '🏠 Homepage Top Banner' },
    { value: 'home_middle', label: '🏠 Homepage Middle' },
    { value: 'browse_sidebar', label: '🔍 Browse Page Sidebar' },
    { value: 'listing_page', label: '📦 Listing Detail Page' }
  ];

  useEffect(() => {
    fetchAds();
  }, []);

  const fetchAds = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await api.get('/admin/banner-ads', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAds(response.data.ads || []);
      setStats(response.data.stats);
    } catch (error) {
      toast.error('Failed to load banner ads');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      if (editingAd) {
        await api.put(`/admin/banner-ads/${editingAd.ad_id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Ad updated successfully');
      } else {
        await api.post('/admin/banner-ads', formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Ad created successfully');
      }
      setShowForm(false);
      setEditingAd(null);
      resetForm();
      fetchAds();
    } catch (error) {
      toast.error('Failed to save ad');
    }
  };

  const handleDelete = async (adId) => {
    if (!confirm('Are you sure you want to delete this ad?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      await api.delete(`/admin/banner-ads/${adId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ad deleted');
      fetchAds();
    } catch (error) {
      toast.error('Failed to delete ad');
    }
  };

  const handleEdit = (ad) => {
    setEditingAd(ad);
    setFormData({
      title: ad.title || '',
      image_url: ad.image_url || '',
      link_url: ad.link_url || '',
      position: ad.position || 'home_top',
      advertiser_name: ad.advertiser_name || '',
      advertiser_email: ad.advertiser_email || '',
      price_paid: ad.price_paid || '',
      start_date: ad.start_date?.split('T')[0] || '',
      end_date: ad.end_date?.split('T')[0] || '',
      is_active: ad.is_active ?? true
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: '', image_url: '', link_url: '', position: 'home_top',
      advertiser_name: '', advertiser_email: '', price_paid: '',
      start_date: '', end_date: '', is_active: true
    });
  };

  const toggleActive = async (ad) => {
    try {
      const token = localStorage.getItem('adminToken');
      await api.put(`/admin/banner-ads/${ad.ad_id}`, 
        { is_active: !ad.is_active },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(`Ad ${!ad.is_active ? 'activated' : 'deactivated'}`);
      fetchAds();
    } catch (error) {
      toast.error('Failed to update ad');
    }
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-banner-ads-tab">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats?.total_ads || 0}</p>
            <p className="text-xs text-muted-foreground">Total Ads</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">{stats?.active_ads || 0}</p>
            <p className="text-xs text-muted-foreground">Active</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-purple-600">${stats?.total_revenue?.toFixed(2) || '0.00'}</p>
            <p className="text-xs text-muted-foreground">Revenue</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{stats?.total_impressions?.toLocaleString() || 0}</p>
            <p className="text-xs text-muted-foreground">Impressions</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-rose-600">{stats?.avg_ctr || 0}%</p>
            <p className="text-xs text-muted-foreground">CTR</p>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">📢 Banner Ads</h3>
        <Button onClick={() => { resetForm(); setEditingAd(null); setShowForm(true); }} className="bg-emerald-500">
          + New Ad
        </Button>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <Card className="border-2 border-emerald-200">
          <CardHeader>
            <CardTitle>{editingAd ? '✏️ Edit Ad' : '➕ Create New Ad'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Title</Label>
                  <Input 
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Ad campaign name"
                    required
                  />
                </div>
                <div>
                  <Label>Position</Label>
                  <select 
                    value={formData.position}
                    onChange={(e) => setFormData({...formData, position: e.target.value})}
                    className="w-full h-10 px-3 rounded-md border bg-white"
                  >
                    {positions.map(pos => (
                      <option key={pos.value} value={pos.value}>{pos.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <Label>Image URL</Label>
                <Input 
                  value={formData.image_url}
                  onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                  placeholder="https://..."
                  required
                />
                {formData.image_url && (
                  <img src={formData.image_url} alt="Preview" className="mt-2 h-20 object-contain rounded" />
                )}
              </div>
              
              <div>
                <Label>Link URL</Label>
                <Input 
                  value={formData.link_url}
                  onChange={(e) => setFormData({...formData, link_url: e.target.value})}
                  placeholder="https://advertiser-website.com"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Advertiser Name</Label>
                  <Input 
                    value={formData.advertiser_name}
                    onChange={(e) => setFormData({...formData, advertiser_name: e.target.value})}
                    placeholder="Company name"
                  />
                </div>
                <div>
                  <Label>Advertiser Email</Label>
                  <Input 
                    type="email"
                    value={formData.advertiser_email}
                    onChange={(e) => setFormData({...formData, advertiser_email: e.target.value})}
                    placeholder="contact@company.com"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Price Paid ($)</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={formData.price_paid}
                    onChange={(e) => setFormData({...formData, price_paid: parseFloat(e.target.value) || 0})}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label>Start Date</Label>
                  <Input 
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input 
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                />
                <Label htmlFor="is_active">Active</Label>
              </div>
              
              <div className="flex gap-2">
                <Button type="submit" className="bg-emerald-500">
                  {editingAd ? 'Update Ad' : 'Create Ad'}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingAd(null); }}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Ads List */}
      <Card>
        <CardContent className="p-4">
          {ads.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Image className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No banner ads yet. Create your first ad!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {ads.map((ad) => (
                <div key={ad.ad_id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                  <img 
                    src={ad.image_url} 
                    alt={ad.title} 
                    className="w-24 h-16 object-cover rounded"
                    onError={(e) => e.target.src = 'https://via.placeholder.com/96x64?text=No+Image'}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{ad.title}</p>
                      <Badge className={ad.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200'}>
                        {ad.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      <Badge variant="outline">
                        {positions.find(p => p.value === ad.position)?.label || ad.position}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{ad.advertiser_name}</p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                      <span>👁️ {ad.impression_count?.toLocaleString() || 0} impressions</span>
                      <span>🖱️ {ad.click_count?.toLocaleString() || 0} clicks</span>
                      <span>💰 ${ad.price_paid?.toFixed(2) || '0.00'}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => toggleActive(ad)}>
                      {ad.is_active ? <X className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleEdit(ad)}>
                      ✏️
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(ad.ad_id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Moderation Tab Component - Disputes, Verifications, Reports
const ModerationTab = () => {
  const [activeSection, setActiveSection] = useState('disputes');
  const [disputes, setDisputes] = useState([]);
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchModerationData();
  }, []);

  const fetchModerationData = async () => {
    setLoading(true);
    try {
      const [disputesRes, verificationsRes, reportsRes] = await Promise.all([
        api.get('/admin/disputes').catch(() => ({ data: { disputes: [] } })),
        api.get('/admin/listings/pending-verification').catch(() => ({ data: { pending_verifications: [] } })),
        api.get('/admin/reports').catch(() => ({ data: { reports: [] } }))
      ]);
      setDisputes(disputesRes.data.disputes || []);
      setPendingVerifications(verificationsRes.data.pending_verifications || []);
      setReports(reportsRes.data.reports || []);
    } catch (error) {
      console.error('Error fetching moderation data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResolveDispute = async (disputeId, resolution) => {
    setActionLoading(disputeId);
    try {
      await api.post(`/admin/disputes/${disputeId}/resolve`, {
        resolution,
        notes: `Resolved in ${resolution}'s favor by admin`
      });
      toast.success(`Dispute resolved in ${resolution}'s favor`);
      fetchModerationData();
    } catch (error) {
      toast.error('Failed to resolve dispute');
    } finally {
      setActionLoading(null);
    }
  };

  const handleVerifyListing = async (listingId, action) => {
    setActionLoading(listingId);
    try {
      await api.post(`/admin/listings/${listingId}/verify`, {
        action,
        reason: action === 'reject' ? 'Verification image does not meet requirements' : ''
      });
      toast.success(`Listing ${action === 'approve' ? 'verified' : 'rejected'}`);
      fetchModerationData();
    } catch (error) {
      toast.error('Failed to process verification');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReportAction = async (reportId, action) => {
    setActionLoading(reportId);
    try {
      await api.post(`/admin/reports/${reportId}/action`, { action });
      toast.success(`Report ${action}ed`);
      fetchModerationData();
    } catch (error) {
      toast.error('Failed to process report');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <Card className="p-8 text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-emerald-500" />
        <p className="mt-2 text-muted-foreground">Loading moderation data...</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 bg-gradient-to-r from-red-50 to-orange-50 border-red-200 cursor-pointer hover:shadow-md" onClick={() => setActiveSection('disputes')}>
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-8 w-8 text-red-500" />
            <div>
              <p className="text-2xl font-bold text-red-700">{disputes.filter(d => d.status === 'open').length}</p>
              <p className="text-sm text-red-600">Open Disputes</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200 cursor-pointer hover:shadow-md" onClick={() => setActiveSection('verifications')}>
          <div className="flex items-center gap-3">
            <FileCheck className="h-8 w-8 text-amber-500" />
            <div>
              <p className="text-2xl font-bold text-amber-700">{pendingVerifications.length}</p>
              <p className="text-sm text-amber-600">Pending Verifications</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 cursor-pointer hover:shadow-md" onClick={() => setActiveSection('reports')}>
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8 text-blue-500" />
            <div>
              <p className="text-2xl font-bold text-blue-700">{reports.filter(r => r.status === 'pending').length}</p>
              <p className="text-sm text-blue-600">Pending Reports</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 bg-white rounded-lg p-1 border">
        {[
          { id: 'disputes', label: 'Disputes', icon: AlertTriangle, count: disputes.filter(d => d.status === 'open').length },
          { id: 'verifications', label: 'Verifications', icon: FileCheck, count: pendingVerifications.length },
          { id: 'reports', label: 'Reports', icon: Shield, count: reports.filter(r => r.status === 'pending').length }
        ].map(section => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors ${
              activeSection === section.id
                ? 'bg-emerald-100 text-emerald-700'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <section.icon className="h-4 w-4" />
            {section.label}
            {section.count > 0 && (
              <Badge className="bg-red-500 text-white text-xs">{section.count}</Badge>
            )}
          </button>
        ))}
      </div>

      {/* Disputes Section */}
      {activeSection === 'disputes' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Disputes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {disputes.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No disputes found</p>
            ) : (
              <div className="space-y-4">
                {disputes.map(dispute => (
                  <div key={dispute.dispute_id} className="p-4 border rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold">{dispute.listing_title}</h4>
                          <Badge className={dispute.status === 'open' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}>
                            {dispute.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-600 mb-2">
                          <strong>Reason:</strong> {dispute.reason?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </p>
                        <p className="text-sm text-slate-600 mb-2">
                          <strong>Description:</strong> {dispute.description || 'No description provided'}
                        </p>
                        <div className="flex gap-4 text-sm text-slate-500">
                          <span>Buyer: {dispute.buyer_name}</span>
                          <span>Seller: {dispute.seller_name}</span>
                          <span>Amount: ${dispute.amount?.toFixed(2)}</span>
                        </div>
                      </div>
                      {dispute.listing_image && (
                        <img src={dispute.listing_image} alt="" className="w-20 h-20 object-cover rounded-lg ml-4" />
                      )}
                    </div>
                    {dispute.status === 'open' && (
                      <div className="flex gap-2 mt-4 pt-4 border-t">
                        <Button
                          size="sm"
                          onClick={() => handleResolveDispute(dispute.dispute_id, 'buyer')}
                          disabled={actionLoading === dispute.dispute_id}
                          className="bg-blue-500 hover:bg-blue-600"
                        >
                          {actionLoading === dispute.dispute_id ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                          Refund Buyer
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleResolveDispute(dispute.dispute_id, 'seller')}
                          disabled={actionLoading === dispute.dispute_id}
                          className="bg-emerald-500 hover:bg-emerald-600"
                        >
                          Release to Seller
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Verifications Section */}
      {activeSection === 'verifications' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-amber-500" />
              Pending Listing Verifications
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pendingVerifications.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No pending verifications</p>
            ) : (
              <div className="space-y-4">
                {pendingVerifications.map(v => (
                  <div key={v.listing_id} className="p-4 border rounded-lg">
                    <div className="flex gap-4">
                      <div className="w-32 h-32 bg-slate-100 rounded-lg overflow-hidden">
                        {v.listing_images?.[0] && (
                          <img src={v.listing_images[0]} alt="" className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold mb-1">{v.listing_title}</h4>
                        <p className="text-sm text-slate-600 mb-2">
                          Seller: {v.seller_name} • Price: ${v.listing_price?.toFixed(2)}
                        </p>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="bg-amber-50">
                            Code: <span className="font-mono">{v.verification_code}</span>
                          </Badge>
                          <span className="text-xs text-slate-500">
                            Submitted: {new Date(v.submitted_at).toLocaleDateString()}
                          </span>
                        </div>
                        {v.verification_image && (
                          <div className="mb-3">
                            <p className="text-xs text-slate-500 mb-1">Verification Photo:</p>
                            <img 
                              src={v.verification_image} 
                              alt="Verification" 
                              className="max-w-xs max-h-40 rounded-lg border cursor-pointer hover:opacity-90"
                              onClick={() => window.open(v.verification_image, '_blank')}
                            />
                          </div>
                        )}
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleVerifyListing(v.listing_id, 'approve')}
                            disabled={actionLoading === v.listing_id}
                            className="bg-emerald-500 hover:bg-emerald-600"
                          >
                            {actionLoading === v.listing_id ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleVerifyListing(v.listing_id, 'reject')}
                            disabled={actionLoading === v.listing_id}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Reports Section */}
      {activeSection === 'reports' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-500" />
              User & Listing Reports
            </CardTitle>
          </CardHeader>
          <CardContent>
            {reports.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No reports found</p>
            ) : (
              <div className="space-y-4">
                {reports.map(report => (
                  <div key={report.report_id} className="p-4 border rounded-lg">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={report.item_type === 'listing' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
                            {report.item_type}
                          </Badge>
                          <Badge className={report.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'}>
                            {report.status}
                          </Badge>
                        </div>
                        <p className="text-sm font-medium">{report.reason?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                        <p className="text-sm text-slate-600 mt-1">{report.description || 'No description'}</p>
                        <p className="text-xs text-slate-500 mt-2">
                          Reported by: {report.reporter_name || 'Anonymous'} • {new Date(report.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      {report.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleReportAction(report.report_id, 'resolve')}
                            disabled={actionLoading === report.report_id}
                            className="bg-emerald-500 hover:bg-emerald-600"
                          >
                            Resolve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReportAction(report.report_id, 'dismiss')}
                            disabled={actionLoading === report.report_id}
                          >
                            Dismiss
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Settings Tab - API Keys and Platform Configuration
const SettingsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    stripe_api_key: '',
    stripe_key_status: 'not_configured',
    stripe_last_verified: null
  });
  const [showStripeKey, setShowStripeKey] = useState(false);
  const [newStripeKey, setNewStripeKey] = useState('');
  const [verifyingKey, setVerifyingKey] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await api.get('/admin/platform-settings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStripeKey = async () => {
    if (!newStripeKey.trim()) {
      toast.error('Please enter a Stripe API key');
      return;
    }
    
    if (!newStripeKey.startsWith('sk_live_') && !newStripeKey.startsWith('sk_test_')) {
      toast.error('Invalid Stripe API key format. Must start with sk_live_ or sk_test_');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await api.post('/admin/platform-settings/stripe-key', 
        { stripe_api_key: newStripeKey },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success('Stripe API key saved and verified successfully!');
      setNewStripeKey('');
      setShowStripeKey(false);
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Stripe API key');
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyCurrentKey = async () => {
    setVerifyingKey(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await api.post('/admin/platform-settings/verify-stripe', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.valid) {
        toast.success('Stripe API key is valid and working!');
        fetchSettings();
      } else {
        toast.error('Stripe API key is invalid or expired');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify Stripe key');
    } finally {
      setVerifyingKey(false);
    }
  };

  const handleRemoveStripeKey = async () => {
    if (!confirm('Are you sure you want to remove the Stripe API key? KYC verification will stop working.')) {
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      await api.delete('/admin/platform-settings/stripe-key', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Stripe API key removed');
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove Stripe key');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  const isLiveKey = settings.stripe_api_key?.startsWith('sk_live_');
  const isTestKey = settings.stripe_api_key?.startsWith('sk_test_');
  const hasKey = settings.stripe_key_status !== 'not_configured';

  return (
    <div className="space-y-6" data-testid="admin-settings-tab">
      <h2 className="text-2xl font-bold">Platform Settings</h2>

      {/* Stripe API Key Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-purple-500" />
            Stripe API Key (KYC Identity Verification)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current Status */}
          <div className="p-4 rounded-lg bg-slate-50 border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Current Status</p>
                <div className="flex items-center gap-2 mt-1">
                  {hasKey ? (
                    <>
                      <Badge className={isLiveKey ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                        {isLiveKey ? '🔒 Live Key' : '🧪 Test Key'}
                      </Badge>
                      <Badge className={settings.stripe_key_status === 'valid' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                        {settings.stripe_key_status === 'valid' ? '✓ Verified' : '✗ Invalid'}
                      </Badge>
                    </>
                  ) : (
                    <Badge className="bg-slate-200 text-slate-600">Not Configured</Badge>
                  )}
                </div>
                {settings.stripe_last_verified && (
                  <p className="text-xs text-slate-400 mt-2">
                    Last verified: {new Date(settings.stripe_last_verified).toLocaleString()}
                  </p>
                )}
              </div>
              {hasKey && (
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleVerifyCurrentKey}
                    disabled={verifyingKey}
                  >
                    {verifyingKey ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1" />}
                    Verify
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-red-600 hover:bg-red-50"
                    onClick={handleRemoveStripeKey}
                    disabled={saving}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Remove
                  </Button>
                </div>
              )}
            </div>
            
            {hasKey && (
              <div className="mt-3 pt-3 border-t">
                <p className="text-sm text-slate-600">
                  <Key className="h-4 w-4 inline mr-1" />
                  Key: <code className="bg-slate-200 px-2 py-0.5 rounded text-xs">
                    {settings.stripe_api_key?.slice(0, 12)}...{settings.stripe_api_key?.slice(-4)}
                  </code>
                </p>
              </div>
            )}
          </div>

          {/* Add/Update Key Form */}
          <div className="border-t pt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowStripeKey(!showStripeKey)}
              className="w-full justify-start"
            >
              <Key className="h-4 w-4 mr-2" />
              {hasKey ? 'Update Stripe API Key' : 'Add Stripe API Key'}
            </Button>
            
            {showStripeKey && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="mb-3">
                  <Label className="text-sm font-medium">Stripe Secret API Key</Label>
                  <p className="text-xs text-slate-500 mb-2">
                    Get your API key from <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Stripe Dashboard → API Keys</a>
                  </p>
                  <Input
                    type="password"
                    value={newStripeKey}
                    onChange={(e) => setNewStripeKey(e.target.value)}
                    placeholder="sk_live_... or sk_test_..."
                    className="font-mono"
                    data-testid="stripe-api-key-input"
                  />
                </div>
                
                <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-800 mb-3">
                  <AlertTriangle className="h-4 w-4 inline mr-1" />
                  <strong>Important:</strong> Use a <strong>Live</strong> key (sk_live_) for real KYC verification. Test keys only work with Stripe's test identity documents.
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    onClick={handleSaveStripeKey}
                    disabled={saving || !newStripeKey.trim()}
                    className="bg-purple-500 hover:bg-purple-600"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Save & Verify Key
                  </Button>
                  <Button variant="outline" onClick={() => { setShowStripeKey(false); setNewStripeKey(''); }}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="p-4 bg-slate-50 rounded-lg text-sm text-slate-600">
            <p className="font-medium mb-2">What is this for?</p>
            <ul className="list-disc ml-4 space-y-1">
              <li>Enables <strong>KYC Identity Verification</strong> using Stripe Identity</li>
              <li>Verifies seller identity with government ID and selfie</li>
              <li>Required for sellers to list high-value items ($500+)</li>
              <li>Increases platform trust and reduces fraud</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Certification Review Tab - Admin reviews inspector certifications
const CertificationReviewTab = () => {
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [selectedCert, setSelectedCert] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');

  useEffect(() => {
    fetchCertifications();
  }, []);

  const fetchCertifications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await api.get('/admin/certifications/pending', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCertifications(res.data.certifications || []);
    } catch (error) {
      console.error('Failed to fetch certifications:', error);
      toast.error('Failed to load certifications');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (certId, action) => {
    setProcessing(certId);
    try {
      const token = localStorage.getItem('adminToken');
      await api.post(`/admin/certifications/${certId}/review`, {
        action,
        notes: reviewNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Certification ${action === 'approve' ? 'approved' : 'rejected'}`);
      setSelectedCert(null);
      setReviewNotes('');
      fetchCertifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to review certification');
    } finally {
      setProcessing(null);
    }
  };

  const getCertTypeLabel = (type) => {
    const labels = {
      'government_cert': 'Government Certification',
      'professional_license': 'Professional License',
      'insurance': 'Professional Insurance',
      'background_check': 'Background Check',
      'training_cert': 'Training Certificate',
      'portfolio': 'Portfolio/Experience'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Inspector Certifications</h2>
        <Badge className="bg-amber-100 text-amber-700">
          {certifications.length} Pending
        </Badge>
      </div>

      {certifications.length === 0 ? (
        <Card className="p-8 text-center">
          <ShieldCheck className="h-12 w-12 mx-auto text-emerald-500 mb-4" />
          <p className="text-slate-600">No certifications pending review</p>
          <p className="text-sm text-slate-400 mt-1">All certifications have been processed</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {certifications.map((cert) => (
            <Card key={cert.cert_id} className="p-4">
              <div className="flex items-start gap-4">
                {/* Document Preview */}
                <div className="w-24 h-24 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0 border">
                  {cert.document_url ? (
                    <a href={cert.document_url} target="_blank" rel="noopener noreferrer">
                      <img 
                        src={cert.document_url} 
                        alt="Certificate" 
                        className="w-full h-full object-cover cursor-pointer hover:opacity-80"
                        onError={(e) => e.target.src = '/placeholder-doc.png'}
                      />
                    </a>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-400">
                      <FileCheck className="h-8 w-8" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-slate-800">{cert.name}</h3>
                    <Badge variant="outline" className="text-xs">
                      {getCertTypeLabel(cert.cert_type)}
                    </Badge>
                  </div>
                  
                  <div className="text-sm text-slate-500 space-y-1">
                    <p><strong>Inspector:</strong> {cert.inspector_name}</p>
                    <p><strong>Email:</strong> {cert.inspector_email}</p>
                    {cert.issuer && <p><strong>Issuer:</strong> {cert.issuer}</p>}
                    <p><strong>Uploaded:</strong> {new Date(cert.uploaded_at).toLocaleDateString()}</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-2">
                  {cert.document_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(cert.document_url, '_blank')}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  )}
                  <Button
                    size="sm"
                    className="bg-emerald-500 hover:bg-emerald-600"
                    onClick={() => handleReview(cert.cert_id, 'approve')}
                    disabled={processing === cert.cert_id}
                  >
                    {processing === cert.cert_id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4 mr-1" />
                    )}
                    Approve
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:bg-red-50"
                    onClick={() => setSelectedCert(cert)}
                    disabled={processing === cert.cert_id}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Reject
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Rejection Modal */}
      {selectedCert && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-white">
            <CardHeader>
              <CardTitle className="text-red-600 flex items-center gap-2">
                <X className="h-5 w-5" />
                Reject Certification
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-600">
                Rejecting certification <strong>"{selectedCert.name}"</strong> from {selectedCert.inspector_name}.
              </p>
              <div>
                <Label>Rejection Reason (will be sent to inspector)</Label>
                <Textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="e.g., Document is unclear, expired, or doesn't match name..."
                  rows={3}
                  className="mt-1"
                />
              </div>
            </CardContent>
            <div className="flex justify-end gap-2 p-4 border-t">
              <Button variant="outline" onClick={() => { setSelectedCert(null); setReviewNotes(''); }}>
                Cancel
              </Button>
              <Button 
                className="bg-red-500 hover:bg-red-600"
                onClick={() => handleReview(selectedCert.cert_id, 'reject')}
                disabled={processing === selectedCert.cert_id}
              >
                {processing === selectedCert.cert_id && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Reject Certification
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const usersPerPage = 25;
  const [activities, setActivities] = useState([]);
  const [listings, setListings] = useState([]);
  const [sales, setSales] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [listingsPage, setListingsPage] = useState(1);
  const [listingsSearch, setListingsSearch] = useState('');
  const [totalListings, setTotalListings] = useState(0);
  const listingsPerPage = 50;

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      setIsLoggedIn(true);
      fetchDashboardData(token);
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      console.log('Attempting admin login...');
      const res = await api.post('/admin/login', { password });
      console.log('Login successful, token received');
      localStorage.setItem('adminToken', res.data.token);
      setIsLoggedIn(true);
      fetchDashboardData(res.data.token);
      toast.success('Admin login successful');
    } catch (error) {
      console.error('Admin login failed:', error.response?.data || error.message);
      toast.error('Invalid admin password');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsLoggedIn(false);
    setStats(null);
  };

  const fetchDashboardData = async (token) => {
    setLoading(true);
    setError(null);
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      // Fetch dashboard stats first (most important)
      const statsRes = await api.get('/admin/dashboard', { headers });
      setStats(statsRes.data);
      console.log('Dashboard stats loaded:', statsRes.data);
    } catch (error) {
      console.error('Dashboard stats error:', error.response?.data || error.message);
      // If any auth error, clear token and force re-login
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('adminToken');
        setIsLoggedIn(false);
        setError('Invalid or expired session. Please login again.');
        toast.error('Please login again');
        setLoading(false);
        return;
      }
      setError(`Failed to load stats: ${error.response?.data?.detail || error.message}`);
      toast.error('Failed to load dashboard stats');
    }
    
    // Fetch other data (non-blocking)
    try {
      const usersRes = await api.get('/admin/users?limit=25', { headers });
      setUsers(usersRes.data.users || []);
      setTotalUsers(usersRes.data.total || 0);
    } catch (e) {
      console.error('Users fetch error:', e.response?.data || e.message);
    }
    
    try {
      const activityRes = await api.get('/admin/activity?limit=30', { headers });
      setActivities(activityRes.data.activities || []);
    } catch (e) {
      console.error('Activity fetch error:', e.response?.data || e.message);
    }
    
    try {
      const listingsRes = await api.get('/admin/listings?limit=1000', { headers });
      setListings(listingsRes.data.listings || []);
      setTotalListings(listingsRes.data.total || 0);
    } catch (e) {
      console.error('Listings fetch error:', e.response?.data || e.message);
    }
    
    try {
      const salesRes = await api.get('/admin/sales?limit=20', { headers });
      setSales(salesRes.data || []);
    } catch (e) {
      console.error('Sales fetch error:', e.response?.data || e.message);
    }
    
    setLoading(false);
  };

  const fetchUsers = async (page = 1, search = '') => {
    const token = localStorage.getItem('adminToken');
    try {
      const skip = (page - 1) * usersPerPage;
      const res = await api.get(`/admin/users?skip=${skip}&limit=${usersPerPage}${search ? `&search=${search}` : ''}`, { 
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data.users || []);
      setTotalUsers(res.data.total || 0);
      setUsersPage(page);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const searchUsers = async () => {
    await fetchUsers(1, searchTerm);
  };

  const formatJoinDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const deleteListing = async (listingId) => {
    if (!window.confirm('Are you sure you want to delete this listing? This action cannot be undone.')) {
      return;
    }
    
    const token = localStorage.getItem('adminToken');
    try {
      await api.delete(`/admin/listings/${listingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Listing deleted successfully');
      // Remove from local state
      setListings(prev => prev.filter(l => l.listing_id !== listingId));
    } catch (error) {
      toast.error('Failed to delete listing');
      console.error('Delete error:', error);
    }
  };

  const approveListing = async (listingId) => {
    const token = localStorage.getItem('adminToken');
    try {
      await api.post(`/admin/listings/${listingId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Listing approved successfully');
      // Update in local state
      setListings(prev => prev.map(l => 
        l.listing_id === listingId ? { ...l, status: 'active' } : l
      ));
    } catch (error) {
      toast.error('Failed to approve listing');
      console.error('Approve error:', error);
    }
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4" data-testid="admin-login">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-16 w-16 bg-emerald-100 rounded-full flex items-center justify-center">
              <Shield className="h-8 w-8 text-emerald-600" />
            </div>
            <CardTitle className="text-2xl">Admin Dashboard</CardTitle>
            <p className="text-muted-foreground">Enter admin password to continue</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label>Password</Label>
                <Input 
                  type="password" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  required
                  data-testid="admin-password-input"
                />
              </div>
              <Button type="submit" className="w-full bg-emerald-500 hover:bg-emerald-600" data-testid="admin-login-btn">
                Login to Dashboard
              </Button>
            </form>
            <div className="mt-4 text-center">
              <Button variant="ghost" onClick={() => navigate('/')}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Back to Site
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin h-12 w-12 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <div className="mx-auto mb-4 h-16 w-16 bg-red-100 rounded-full flex items-center justify-center">
              <Shield className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-red-600 mb-2">Error Loading Dashboard</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <div className="flex gap-2 justify-center">
              <Button onClick={() => fetchDashboardData(localStorage.getItem('adminToken'))} className="bg-emerald-500 hover:bg-emerald-600">
                Retry
              </Button>
              <Button variant="outline" onClick={handleLogout}>
                Logout
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      {/* Admin Header */}
      <div className="bg-slate-900 text-white px-6 py-4">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Shield className="h-8 w-8 text-emerald-400" />
            <div>
              <h1 className="text-xl font-bold">BidZuu Admin</h1>
              <p className="text-slate-400 text-sm">Dashboard & Management</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" className="text-white hover:bg-slate-800" onClick={() => navigate('/')}>
              <ExternalLink className="h-4 w-4 mr-2" /> View Site
            </Button>
            <Button variant="ghost" className="text-white hover:bg-slate-800" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="container mx-auto">
          <div className="flex gap-1 overflow-x-auto">
            {[
              { id: 'overview', label: 'Overview', icon: BarChart3 },
              { id: 'analytics', label: 'Analytics', icon: Activity },
              { id: 'moderation', label: 'Moderation', icon: Shield },
              { id: 'certifications', label: 'Certifications', icon: FileCheck },
              { id: 'users', label: 'Users', icon: Users },
              { id: 'listings', label: 'Listings', icon: Package },
              { id: 'sales', label: 'Sales', icon: DollarSign },
              { id: 'payouts', label: 'Payouts', icon: DollarSign },
              { id: 'ads', label: 'Banner Ads', icon: Image },
              { id: 'activity', label: 'Activity', icon: Activity },
              { id: 'import', label: 'URL Import', icon: Link },
              { id: 'tiktok', label: 'TikTok Videos', icon: Activity },
              { id: 'settings', label: 'Settings', icon: Settings }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${
                  activeTab === tab.id 
                    ? 'text-emerald-600 border-b-2 border-emerald-600' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid={`admin-tab-${tab.id}`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="container mx-auto p-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && !stats && (
          <Card className="p-8 text-center">
            <p className="text-muted-foreground">No dashboard data available. Try refreshing.</p>
            <Button onClick={() => fetchDashboardData(localStorage.getItem('adminToken'))} className="mt-4">
              Refresh Data
            </Button>
          </Card>
        )}
        {activeTab === 'overview' && stats && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100">Total Users</p>
                      <p className="text-3xl font-bold">{stats.users.total}</p>
                      <p className="text-blue-200 text-sm">+{stats.users.today} today</p>
                    </div>
                    <Users className="h-12 w-12 text-blue-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-emerald-100">Active Listings</p>
                      <p className="text-3xl font-bold">{stats.listings.active}</p>
                      <p className="text-emerald-200 text-sm">+{stats.listings.today} today</p>
                    </div>
                    <Package className="h-12 w-12 text-emerald-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100">Total Sales</p>
                      <p className="text-3xl font-bold">{stats.sales.total}</p>
                      <p className="text-purple-200 text-sm">+{stats.sales.today} today</p>
                    </div>
                    <ShoppingBag className="h-12 w-12 text-purple-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-amber-100">Fees Collected</p>
                      <p className="text-3xl font-bold">${(stats.sales.total_fees_collected || 0).toFixed(2)}</p>
                      <p className="text-amber-200 text-sm">10% commission</p>
                    </div>
                    <DollarSign className="h-12 w-12 text-amber-200" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Secondary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center">
                      <Gavel className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <p className="text-muted-foreground">Active Auctions</p>
                      <p className="text-2xl font-bold">{stats.listings.auctions}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                      <Tag className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-muted-foreground">Fixed Price Items</p>
                      <p className="text-2xl font-bold">{stats.listings.fixed_price}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <MessageCircle className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-muted-foreground">Total Messages</p>
                      <p className="text-2xl font-bold">{stats.activity.total_messages}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Categories Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Top Categories</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {stats.categories.map((cat, i) => (
                    <div key={i} className="flex items-center gap-4">
                      <div className="w-32 text-sm font-medium capitalize">{cat.name || 'Other'}</div>
                      <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                        <div 
                          className="bg-emerald-500 h-full rounded-full"
                          style={{ width: `${(cat.count / (stats.categories[0]?.count || 1)) * 100}%` }}
                        />
                      </div>
                      <div className="w-12 text-right text-sm font-medium">{cat.count}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {activities.slice(0, 10).map((activity, i) => (
                    <div key={i} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                        activity.type === 'registration' ? 'bg-blue-100' :
                        activity.type === 'listing' ? 'bg-emerald-100' :
                        'bg-amber-100'
                      }`}>
                        {activity.type === 'registration' && <UserPlus className="h-5 w-5 text-blue-600" />}
                        {activity.type === 'listing' && <Package className="h-5 w-5 text-emerald-600" />}
                        {activity.type === 'sale' && <DollarSign className="h-5 w-5 text-amber-600" />}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{activity.message}</p>
                        <p className="text-sm text-muted-foreground">
                          {activity.timestamp ? new Date(activity.timestamp).toLocaleString() : 'Recently'}
                        </p>
                      </div>
                      {activity.type === 'sale' && (
                        <Badge className="bg-emerald-100 text-emerald-700">${activity.amount?.toFixed(2)}</Badge>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      All Users ({totalUsers})
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Complete list of all registered users since platform launch
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Input 
                      placeholder="Search by name or email..." 
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
                      className="w-64"
                      data-testid="admin-user-search"
                    />
                    <Button onClick={searchUsers}>
                      <Search className="h-4 w-4" />
                    </Button>
                    {searchTerm && (
                      <Button variant="outline" onClick={() => { setSearchTerm(''); fetchUsers(1); }}>
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left p-3 font-medium">#</th>
                        <th className="text-left p-3 font-medium">User</th>
                        <th className="text-left p-3 font-medium">Email</th>
                        <th className="text-left p-3 font-medium">Joined</th>
                        <th className="text-left p-3 font-medium">Listings</th>
                        <th className="text-left p-3 font-medium">Sales</th>
                        <th className="text-left p-3 font-medium">Purchases</th>
                        <th className="text-left p-3 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user, index) => (
                        <tr key={user.user_id} className="border-b hover:bg-slate-50">
                          <td className="p-3 text-muted-foreground text-sm">
                            {(usersPage - 1) * usersPerPage + index + 1}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-8 w-8">
                                {user.picture ? (
                                  <AvatarImage src={user.picture} />
                                ) : (
                                  <AvatarFallback className="bg-emerald-100 text-emerald-700">
                                    {user.name?.charAt(0)?.toUpperCase()}
                                  </AvatarFallback>
                                )}
                              </Avatar>
                              <div>
                                <span className="font-medium">{user.name}</span>
                                {user.is_verified && (
                                  <Badge className="ml-2 bg-blue-100 text-blue-700 text-xs">Verified</Badge>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="p-3 text-muted-foreground">{user.email}</td>
                          <td className="p-3">
                            <div className="flex flex-col">
                              <span className="text-sm font-medium">{formatJoinDate(user.created_at)}</span>
                              <span className="text-xs text-muted-foreground">
                                {user.created_at ? new Date(user.created_at).toLocaleDateString() : ''}
                              </span>
                            </div>
                          </td>
                          <td className="p-3">
                            <Badge variant="outline">{user.listing_count || 0}</Badge>
                          </td>
                          <td className="p-3">
                            <Badge className="bg-emerald-100 text-emerald-700">{user.sales_count || 0}</Badge>
                          </td>
                          <td className="p-3">
                            <Badge className="bg-blue-100 text-blue-700">{user.purchase_count || 0}</Badge>
                          </td>
                          <td className="p-3">
                            <Badge className={user.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                              {user.status || 'active'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {totalUsers > usersPerPage && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Showing {(usersPage - 1) * usersPerPage + 1} - {Math.min(usersPage * usersPerPage, totalUsers)} of {totalUsers} users
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        disabled={usersPage === 1}
                        onClick={() => fetchUsers(usersPage - 1, searchTerm)}
                      >
                        Previous
                      </Button>
                      <span className="flex items-center px-3 text-sm">
                        Page {usersPage} of {Math.ceil(totalUsers / usersPerPage)}
                      </span>
                      <Button 
                        variant="outline" 
                        size="sm"
                        disabled={usersPage >= Math.ceil(totalUsers / usersPerPage)}
                        onClick={() => fetchUsers(usersPage + 1, searchTerm)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}

                {users.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No users found</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Listings Tab */}
        {activeTab === 'listings' && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <CardTitle>All Listings ({totalListings || listings.length})</CardTitle>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Search listings..."
                      value={listingsSearch}
                      onChange={(e) => setListingsSearch(e.target.value)}
                      className="w-64"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left p-3 font-medium">Item</th>
                        <th className="text-left p-3 font-medium">Seller</th>
                        <th className="text-left p-3 font-medium">Location</th>
                        <th className="text-left p-3 font-medium">Type</th>
                        <th className="text-left p-3 font-medium">Price</th>
                        <th className="text-left p-3 font-medium">Status</th>
                        <th className="text-left p-3 font-medium">Created</th>
                        <th className="text-left p-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {listings
                        .filter(l => !listingsSearch || l.title?.toLowerCase().includes(listingsSearch.toLowerCase()) || l.seller_name?.toLowerCase().includes(listingsSearch.toLowerCase()))
                        .slice((listingsPage - 1) * listingsPerPage, listingsPage * listingsPerPage)
                        .map((listing) => (
                        <tr key={listing.listing_id} className="border-b hover:bg-slate-50">
                          <td className="p-3">
                            <div className="flex items-center gap-3">
                              {listing.images?.[0] ? (
                                <img src={listing.images[0]} alt="" className="h-10 w-10 object-cover rounded" />
                              ) : (
                                <div className="h-10 w-10 bg-slate-100 rounded flex items-center justify-center">
                                  <Package className="h-5 w-5 text-slate-400" />
                                </div>
                              )}
                              <span className="font-medium truncate max-w-[200px]">{listing.title}</span>
                            </div>
                          </td>
                          <td className="p-3">
                            <span className="text-sm">{listing.seller_name || 'Unknown'}</span>
                          </td>
                          <td className="p-3">
                            <span className="text-sm text-muted-foreground">
                              {listing.location?.city ? `${listing.location.city}, ${listing.location.state || listing.location.country || ''}` : 'Not set'}
                            </span>
                          </td>
                          <td className="p-3">
                            <Badge className={listing.listing_type === 'auction' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
                              {listing.listing_type}
                            </Badge>
                          </td>
                          <td className="p-3 font-medium">${listing.price?.toFixed(2)}</td>
                          <td className="p-3">
                            <Badge className={
                              listing.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 
                              listing.status === 'pending_review' ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-700'
                            }>
                              {listing.status}
                            </Badge>
                          </td>
                          <td className="p-3 text-muted-foreground text-sm">
                            {listing.created_at ? new Date(listing.created_at).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              {listing.status === 'pending_review' && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => approveListing(listing.listing_id)}
                                  title="Approve listing"
                                  className="text-emerald-600 hover:bg-emerald-50 text-xs"
                                >
                                  <Check className="h-4 w-4 mr-1" />
                                  Approve
                                </Button>
                              )}
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => window.open(`/item/${listing.listing_id}`, '_blank')}
                                title="View listing"
                              >
                                <Eye className="h-4 w-4 text-blue-600" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => deleteListing(listing.listing_id)}
                                title="Delete listing"
                                className="hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {listings.filter(l => !listingsSearch || l.title?.toLowerCase().includes(listingsSearch.toLowerCase())).length > listingsPerPage && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Showing {((listingsPage - 1) * listingsPerPage) + 1} - {Math.min(listingsPage * listingsPerPage, listings.filter(l => !listingsSearch || l.title?.toLowerCase().includes(listingsSearch.toLowerCase())).length)} of {listings.filter(l => !listingsSearch || l.title?.toLowerCase().includes(listingsSearch.toLowerCase())).length} listings
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setListingsPage(p => Math.max(1, p - 1))}
                        disabled={listingsPage === 1}
                      >
                        Previous
                      </Button>
                      {Array.from({ length: Math.min(5, Math.ceil(listings.filter(l => !listingsSearch || l.title?.toLowerCase().includes(listingsSearch.toLowerCase())).length / listingsPerPage)) }, (_, i) => (
                        <Button
                          key={i + 1}
                          variant={listingsPage === i + 1 ? "default" : "outline"}
                          size="sm"
                          onClick={() => setListingsPage(i + 1)}
                          className={listingsPage === i + 1 ? "bg-emerald-500" : ""}
                        >
                          {i + 1}
                        </Button>
                      ))}
                      {Math.ceil(listings.filter(l => !listingsSearch || l.title?.toLowerCase().includes(listingsSearch.toLowerCase())).length / listingsPerPage) > 5 && (
                        <>
                          <span className="px-2">...</span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setListingsPage(Math.ceil(listings.filter(l => !listingsSearch || l.title?.toLowerCase().includes(listingsSearch.toLowerCase())).length / listingsPerPage))}
                          >
                            {Math.ceil(listings.filter(l => !listingsSearch || l.title?.toLowerCase().includes(listingsSearch.toLowerCase())).length / listingsPerPage)}
                          </Button>
                        </>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setListingsPage(p => Math.min(Math.ceil(listings.filter(l => !listingsSearch || l.title?.toLowerCase().includes(listingsSearch.toLowerCase())).length / listingsPerPage), p + 1))}
                        disabled={listingsPage >= Math.ceil(listings.filter(l => !listingsSearch || l.title?.toLowerCase().includes(listingsSearch.toLowerCase())).length / listingsPerPage)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Sales Tab */}
        {activeTab === 'sales' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-emerald-50 border-emerald-200">
                <CardContent className="p-6">
                  <p className="text-emerald-600 font-medium">Total Revenue</p>
                  <p className="text-3xl font-bold text-emerald-700">${(sales.total_revenue || 0).toFixed(2)}</p>
                </CardContent>
              </Card>
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-6">
                  <p className="text-amber-600 font-medium">Fees Collected (10%)</p>
                  <p className="text-3xl font-bold text-amber-700">${(sales.total_fees || 0).toFixed(2)}</p>
                </CardContent>
              </Card>
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-6">
                  <p className="text-blue-600 font-medium">Total Transactions</p>
                  <p className="text-3xl font-bold text-blue-700">{sales.total || 0}</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Recent Sales</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 font-medium">Item</th>
                        <th className="text-left p-3 font-medium">Amount</th>
                        <th className="text-left p-3 font-medium">Fee</th>
                        <th className="text-left p-3 font-medium">Seller Payout</th>
                        <th className="text-left p-3 font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(sales.sales || []).map((sale) => (
                        <tr key={sale.purchase_id} className="border-b hover:bg-slate-50">
                          <td className="p-3 font-medium">{sale.listing_title || 'Item'}</td>
                          <td className="p-3">${(sale.total_paid || 0).toFixed(2)}</td>
                          <td className="p-3 text-amber-600">${(sale.total_fee || 0).toFixed(2)}</td>
                          <td className="p-3 text-emerald-600">${(sale.seller_payout || 0).toFixed(2)}</td>
                          <td className="p-3 text-muted-foreground">
                            {sale.purchased_at ? new Date(sale.purchased_at).toLocaleDateString() : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Import Tab */}
        {activeTab === 'import' && <ImportTab />}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && <AnalyticsTab />}

        {/* Moderation Tab */}
        {activeTab === 'moderation' && <ModerationTab />}
        {activeTab === 'certifications' && <CertificationReviewTab />}

        {/* Payouts Tab */}
        {activeTab === 'payouts' && <PayoutsTab />}

        {/* Banner Ads Tab */}
        {activeTab === 'ads' && <BannerAdsTab />}

        {/* TikTok Videos Management Tab */}
        {activeTab === 'tiktok' && <TikTokManagementTab />}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <Card>
            <CardHeader>
              <CardTitle>Activity Log</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {activities.map((activity, i) => (
                  <div key={i} className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                      activity.type === 'registration' ? 'bg-blue-100' :
                      activity.type === 'listing' ? 'bg-emerald-100' :
                      'bg-amber-100'
                    }`}>
                      {activity.type === 'registration' && <UserPlus className="h-5 w-5 text-blue-600" />}
                      {activity.type === 'listing' && <Package className="h-5 w-5 text-emerald-600" />}
                      {activity.type === 'sale' && <DollarSign className="h-5 w-5 text-amber-600" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{activity.message}</p>
                      <p className="text-sm text-muted-foreground">
                        {activity.timestamp ? new Date(activity.timestamp).toLocaleString() : 'Recently'}
                      </p>
                    </div>
                    <Badge className={
                      activity.type === 'registration' ? 'bg-blue-100 text-blue-700' :
                      activity.type === 'listing' ? 'bg-emerald-100 text-emerald-700' :
                      'bg-amber-100 text-amber-700'
                    }>
                      {activity.type}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && <SettingsTab />}
      </div>
    </div>
  );
};

export default AdminDashboard;
